import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import socket from "../socket";
import { useAppDispatch } from "../store/hooks";
import {
  setConnected,
  setConnecting,
  setRoomJoined,
  setError,
  resetMultiplayer,
  RoomState,
} from "../store/multiplayerSlice";
import { useFarcaster } from "../contexts/FarcasterContext";
// import { APP_BASE_URL } from '../config';
import PlayerAvatar from "../components/multiplayer/PlayerAvatar";
import { SmallButton } from "../components/UI/smallButton/SmallButton";
import { getPlayerId } from "../helpers/identity";
import { media } from "../styles/GlobalStyles";

// ─── Types ────────────────────────────────────────────────────────────────────

interface RoomInfo {
  id: string;
  hostName: string;
  hostPfpUrl?: string;
  hostUsername?: string;
  hostFid?: number;
  isStarted: boolean;
  playerCount: number;
  spectatorCount: number;
  scores: { p1: number; p2: number };
}

// ─── Animations ───────────────────────────────────────────────────────────────

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #1a0533 0%,
    #5c2dd5 35%,
    #7945ff 65%,
    #2d0a6e 100%
  );
  background-size: 300% 300%;
  animation: ${gradientShift} 8s ease infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        circle at 30% 20%,
        rgba(255, 206, 103, 0.08) 0%,
        transparent 60%
      ),
      radial-gradient(
        circle at 70% 80%,
        rgba(253, 102, 135, 0.08) 0%,
        transparent 60%
      );
    pointer-events: none;
  }
`;

const FloatingDisk = styled.div<{
  top: string;
  left: string;
  size: string;
  color: string;
  delay: string;
}>`
  position: absolute;
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  border-radius: 50%;
  background: ${({ color }) => color};
  opacity: 0.15;
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay};
  pointer-events: none;
`;

const Card = styled(motion.div)`
  background: var(--color-white);
  border: 3px solid var(--color-black);
  box-shadow:
    0 1.2rem 0 var(--color-black),
    0 2rem 40px rgba(0, 0, 0, 0.4);
  border-radius: 2.4rem;
  padding: 3.2rem 2.8rem;
  width: 100%;
  max-width: 42rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  position: relative;
  z-index: 1;

  ${media.phone} {
    padding: 2.4rem 2rem;
    border-radius: 2rem;
  }
`;

const GameBadge = styled.div`
  background: linear-gradient(135deg, #5c2dd5, #7945ff);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 10rem;
  padding: 0.4rem 1.4rem;
  color: var(--color-white);
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const AvatarContainer = styled.div`
  position: relative;
  margin-bottom: 1.6rem;
`;

const AvatarGlow = styled.div<{ color: string }>`
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  background: ${({ color }) => color};
  filter: blur(12px);
  opacity: 0.4;
  z-index: 0;
`;

const BigAvatar = styled.div`
  position: relative;
  z-index: 1;
  animation: ${float} 4s ease-in-out infinite;
`;

const HostName = styled.h2`
  font-size: var(--font-size-heading-m);
  color: var(--color-black);
  text-align: center;
  margin-bottom: 0.4rem;
`;

const HostHandle = styled.p`
  font-size: 1.4rem;
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
  margin-bottom: 0.4rem;
`;

const ChallengeText = styled.p`
  font-size: 1.6rem;
  color: var(--color-dark-purple);
  text-align: center;
  font-weight: bold;
  margin-bottom: 2.4rem;
`;

const RoomCodeBox = styled.div`
  background: linear-gradient(135deg, #1a0533, #5c2dd5);
  border-radius: 1.2rem;
  padding: 1rem 2rem;
  margin-bottom: 2rem;
  text-align: center;
  width: 100%;
`;

const RoomCodeLabel = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.2rem;
`;

const RoomCode = styled.p`
  font-size: 2.8rem;
  font-weight: bold;
  color: var(--color-white);
  letter-spacing: 0.3em;
  font-family: monospace;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 0.8rem 0 1.6rem 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: 100%;
  margin-bottom: 1.6rem;
`;

const JoinBtn = styled.button<{ variant: "play" | "spectate" }>`
  width: 100%;
  padding: 1.4rem 1.6rem;
  border: 3px solid var(--color-black);
  box-shadow: 0 0.8rem 0 var(--color-black);
  border-radius: 1.6rem;
  background: ${({ variant }) =>
    variant === "play"
      ? "linear-gradient(135deg, #7945ff, #5c2dd5)"
      : "var(--color-yellow)"};
  color: ${({ variant }) =>
    variant === "play" ? "var(--color-white)" : "var(--color-black)"};
  font-size: var(--font-size-heading-xs);
  font-family: inherit;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;

  &:hover {
    box-shadow: 0 0.8rem 0 var(--color-dark-purple);
    filter: brightness(1.05);
  }
  &:active {
    transform: translateY(3px);
    box-shadow: 0 5px 0 var(--color-dark-purple);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusPills = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: center;
  margin-bottom: 1.6rem;
  flex-wrap: wrap;
`;

const Pill = styled.span<{ color: string }>`
  background: ${({ color }) => color};
  border-radius: 10rem;
  padding: 0.3rem 1rem;
  font-size: 1.2rem;
  color: var(--color-white);
  font-weight: bold;
`;

const NameInput = styled.input`
  width: 100%;
  padding: 1rem 1.4rem;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 1rem;
  font-size: 1.4rem;
  font-family: inherit;
  outline: none;
  background: rgba(0, 0, 0, 0.04);
  margin-bottom: 1.2rem;

  &:focus {
    border-color: var(--color-purple);
    box-shadow: 0 0 0 3px rgba(121, 69, 255, 0.15);
  }
`;

const ErrorText = styled.p`
  color: var(--color-red);
  font-size: 1.3rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 4rem;
  height: 4rem;
  border: 3px solid rgba(121, 69, 255, 0.2);
  border-top-color: var(--color-purple);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 2rem auto;
`;

const FarcasterBrand = styled.p`
  font-size: 1.2rem;
  color: rgba(0, 0, 0, 0.35);
  text-align: center;
  margin-top: 0.4rem;
`;

const BackBtn = styled(SmallButton)`
  margin-top: 1.6rem;
  position: relative;
  z-index: 1;
`;

// ─── Component ────────────────────────────────────────────────────────────────

// The API server URL (Socket.IO + REST endpoints)
const SERVER_API_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:3001");

const InvitePage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user: farcasterUser } = useFarcaster();

  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setLocalError] = useState<string | null>(null);
  const [playerName, setPlayerName] = useState("");
  const [joining, setJoining] = useState(false);
  const [socketReady, setSocketReady] = useState(socket.connected);

  const joinTypeRef = useRef<"p2" | "spectator" | null>(null);

  // Auto-populate name from Farcaster
  useEffect(() => {
    if (farcasterUser) {
      setPlayerName(farcasterUser.displayName || farcasterUser.username || "");
    }
  }, [farcasterUser]);

  // Fetch room info from server
  useEffect(() => {
    if (!code) return;
    setLoading(true);
    fetch(`${SERVER_API_URL}/api/room/${code.toUpperCase()}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) {
          setLocalError(data.error);
        } else {
          setRoomInfo(data);
        }
      })
      .catch(() => setLocalError("Could not reach the game server."))
      .finally(() => setLoading(false));
  }, [code]);

  // Connect socket
  useEffect(() => {
    dispatch(resetMultiplayer());
    if (!socket.connected) socket.connect();

    const onConnect = () => {
      setSocketReady(true);
      dispatch(setConnected(true));
    };
    const onDisconnect = () => {
      setSocketReady(false);
      dispatch(setConnected(false));
    };
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    if (socket.connected) {
      setSocketReady(true);
      dispatch(setConnected(true));
    }
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [dispatch]);

  // Listen for join response
  useEffect(() => {
    const onRoomJoined = (data: {
      roomId: string;
      role: "p2" | "spectator";
      color: "yellow" | null;
      roomState: RoomState;
    }) => {
      dispatch(setRoomJoined(data));
      navigate("/multiplayer/game");
    };
    const onJoinError = ({ message }: { message: string }) => {
      setLocalError(message);
      setJoining(false);
    };
    socket.on("room-joined", onRoomJoined);
    socket.on("join-error", onJoinError);
    return () => {
      socket.off("room-joined", onRoomJoined);
      socket.off("join-error", onJoinError);
    };
  }, [dispatch, navigate]);

  const handleJoin = (type: "p2" | "spectator") => {
    if (!socketReady || !code) return;
    joinTypeRef.current = type;
    const name =
      playerName.trim() ||
      farcasterUser?.displayName ||
      farcasterUser?.username ||
      (type === "spectator" ? "Spectator" : "Player");

    setJoining(true);
    dispatch(setConnecting(true));
    dispatch(setError(null));
    socket.emit("join-room", {
      roomId: code.toUpperCase(),
      playerName: name,
      fid: farcasterUser?.fid,
      pfpUrl: farcasterUser?.pfpUrl,
      username: farcasterUser?.username,
      playerId: getPlayerId(),
      joinAs: type,
    });
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  const canJoinAsPlayer =
    roomInfo && !roomInfo.isStarted && roomInfo.playerCount < 2;

  return (
    <Page>
      {/* Floating background discs */}
      <FloatingDisk
        top="10%"
        left="5%"
        size="6rem"
        color="#fd6687"
        delay="0s"
      />
      <FloatingDisk
        top="70%"
        left="85%"
        size="8rem"
        color="#ffce67"
        delay="1s"
      />
      <FloatingDisk
        top="40%"
        left="90%"
        size="4rem"
        color="#7945ff"
        delay="2s"
      />
      <FloatingDisk
        top="80%"
        left="10%"
        size="5rem"
        color="#7945ff"
        delay="0.5s"
      />

      <Card
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <GameBadge>⬡ Connect Four</GameBadge>

        {loading ? (
          <LoadingSpinner />
        ) : error && !roomInfo ? (
          <>
            <p style={{ fontSize: "4rem", marginBottom: "1rem" }}>😕</p>
            <HostName>Game Not Found</HostName>
            <HostHandle>{error}</HostHandle>
          </>
        ) : roomInfo ? (
          <>
            {/* Host PFP */}
            <AvatarContainer>
              <AvatarGlow color="linear-gradient(135deg,#7945ff,#fd6687)" />
              <BigAvatar>
                <PlayerAvatar
                  pfpUrl={roomInfo.hostPfpUrl}
                  name={roomInfo.hostName}
                  size={96}
                  ringColor="#7945ff"
                  isActive={false}
                />
              </BigAvatar>
            </AvatarContainer>

            <HostName>
              {roomInfo.hostUsername
                ? `@${roomInfo.hostUsername}`
                : roomInfo.hostName}
            </HostName>
            {roomInfo.hostUsername && (
              <HostHandle>{roomInfo.hostName}</HostHandle>
            )}
            <ChallengeText>challenges you to Connect Four! 🔴🟡</ChallengeText>

            {/* Room stats pills */}
            <StatusPills>
              <Pill color={roomInfo.isStarted ? "#22c55e" : "#f59e0b"}>
                {roomInfo.isStarted ? "● Live" : "○ Waiting"}
              </Pill>
              <Pill color="#5c2dd5">👤 {roomInfo.playerCount}/2 Players</Pill>
              {roomInfo.spectatorCount > 0 && (
                <Pill color="#374151">
                  👁 {roomInfo.spectatorCount} watching
                </Pill>
              )}
            </StatusPills>

            {/* Room code */}
            <RoomCodeBox>
              <RoomCodeLabel>Room Code</RoomCodeLabel>
              <RoomCode>{roomInfo.id}</RoomCode>
            </RoomCodeBox>

            {/* Name input (only if not Farcaster user) */}
            {!farcasterUser && (
              <NameInput
                type="text"
                placeholder="Your name (optional)"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                maxLength={20}
              />
            )}

            {error && <ErrorText>{error}</ErrorText>}

            <Divider />

            <ButtonGroup>
              <JoinBtn
                variant="play"
                onClick={() => handleJoin("p2")}
                disabled={!socketReady || joining || !canJoinAsPlayer}
                title={
                  !canJoinAsPlayer
                    ? "Game is full or already started"
                    : undefined
                }
              >
                🎮{" "}
                {joining && joinTypeRef.current === "p2"
                  ? "Joining…"
                  : "Join as Player"}
                {!canJoinAsPlayer && " (Full)"}
              </JoinBtn>
              <JoinBtn
                variant="spectate"
                onClick={() => handleJoin("spectator")}
                disabled={!socketReady || joining}
              >
                👁{" "}
                {joining && joinTypeRef.current === "spectator"
                  ? "Joining…"
                  : "Watch as Spectator"}
              </JoinBtn>
            </ButtonGroup>

            <FarcasterBrand>⬡ Farcaster Mini-App</FarcasterBrand>
          </>
        ) : null}
      </Card>

      <BackBtn onClick={() => navigate("/")}>← Back to Menu</BackBtn>
    </Page>
  );
};

export default InvitePage;
